<template>
  <TemplateLogin>
    <div class="calico-expired-link-container">
      <div
        v-if="!isLoading"
        class="email-info"
      >
        <i class="bi bi-exclamation-triangle-fill" />
        <h3 class="heading--medium">
          Verification Link Expired
        </h3>
        <p class="body--medium">
          Verification links expire in {{ VERIFICATION_EXPIRE_MINUTES }} minutes.
          Send a new link to your email and ensure you verify it in time.
        </p>
        <button
          class="button-primary"
          :disabled="isResendDisabled"
          @click="onClickSendNewLink"
        >
          Send New Link
        </button>
        <p
          v-if="isResendDisabled"
          class="label--small"
        >
          Try again in {{ timerText }}
        </p>
      </div>
      <div
        v-else
        class="calico-loader-container"
      >
        <CalicoLoader />
      </div>
    </div>
  </TemplateLogin>
</template>

<script>
import { getAuth } from "firebase/auth";
import { ref, onMounted } from "vue";
import CalicoLoader from "@/components/calico/loader/index.vue";
import TemplateLogin from "@/components/template/login/index.vue";
import services from "@/services";

export default {
  name: "ExpiredVerificationLink",
  components: {
    TemplateLogin,
    CalicoLoader,
  },
  setup() {
    const auth = getAuth();

    const isResendDisabled = ref(false);
    const isLoading = ref(false);
    const timerText = ref("");
    const hasClickedManually = ref(false);

    const VERIFICATION_EXPIRATION_MS = Number(process.env.VUE_APP_VERIFICATION_EXPIRATION_MS) || 600000;
    const VERIFICATION_EXPIRE_MINUTES = Math.floor(VERIFICATION_EXPIRATION_MS / 60000);

    const COOLDOWN_MS = 180000;
    const COOLDOWN_KEY = "resendCooldownStartedAt";
    let interval = null;

    const formatTime = (ms) => {
      const minutes = Math.floor(ms / 60000);
      const seconds = ((ms % 60000) / 1000).toFixed(0).padStart(2, "0");
      return `${minutes}:${seconds}`;
    };

    const startCountdown = (initialTime = COOLDOWN_MS) => {
      let timeLeft = initialTime;
      isResendDisabled.value = true;
      timerText.value = formatTime(timeLeft);
      clearInterval(interval);
      interval = setInterval(() => {
        timeLeft -= 1000;
        timerText.value = formatTime(timeLeft);
        if (timeLeft <= 0) {
          clearInterval(interval);
          localStorage.removeItem(COOLDOWN_KEY);
          isResendDisabled.value = false;
        }
      }, 1000);
    };

    const checkCooldown = () => {
      const stored = localStorage.getItem(COOLDOWN_KEY);
      if (stored) {
        const elapsed = Date.now() - parseInt(stored, 10);
        if (elapsed < COOLDOWN_MS) {
          const remaining = COOLDOWN_MS - elapsed;
          startCountdown(remaining);
        }
      }
    };

    onMounted(() => {
      checkCooldown();
    });

    const onClickSendNewLink = () => {
      hasClickedManually.value = true;
      resendEmail();
    };

    const resendEmail = async () => {
      if (isResendDisabled.value || !hasClickedManually.value) return;

      isLoading.value = true;
      localStorage.setItem(COOLDOWN_KEY, Date.now().toString());
      startCountdown();

      try {
        const user = auth.currentUser;
        if (!user || !user.email) return;

        await services.auth.sendVerificationEmail(user.email);
      } catch (error) {
        console.log("[resendEmail] Error during resend flow:", error);
      } finally {
        isLoading.value = false;
      }
    };

    return {
      isResendDisabled,
      resendEmail,
      isLoading,
      timerText,
      VERIFICATION_EXPIRE_MINUTES,
      onClickSendNewLink,
    };
  },
};
</script>

<style scoped lang="scss">
.calico-expired-link-container {
  @include inline-space($space-none);
  justify-content: center;
  align-items: center;

  .email-info {
    @include stack-space($space-large);
    max-width: $tablet-breakpoint-min-width;
    padding: $space-large $space-none;
    width: 60%;
    text-align: center;

    .bi {
      font-size: $icon-large;
      color: $color-orange;
    }

    .label--small {
      color: $color-text-secondary;
      font-weight: 400;
    }
  }
}
</style>
